<template>
  <b-modal
    id="modal-bank-record-filter"
    ok-title="Submit"
    cancel-variant="outline-secondary"
    title="過瀘"
    title-class="font-weight-bolder"
    body-class="common_modal modal_visible"
    hide-footer
    no-close-on-backdrop
    size="lg"
    centered
    @shown="onModalShown"
  >
    <b-overlay
      :show="loading"
      spinner-variant="primary"
      spinner-type="grow"
      spinner-small
      rounded="sm"
    >
      <b-form class="mt-1" @submit.prevent="onSubmit">
        <b-row>
          <!-- 金額範圍篩選 -->
          <b-col cols="12">
            <b-form-group
              label="金額範圍："
              label-for="filter_amount_range"
              label-cols-md="3"
            >
              <div class="amount-range-container">
                <vue-slider
                  v-model="update.filter_amount_range"
                  :min="0"
                  :max="1000000"
                  :interval="1"
                  :tooltip="'always'"
                  :tooltip-formatter="formatTooltip"
                  :dot-size="20"
                  :height="6"
                  :process-style="{ backgroundColor: '#007bff' }"
                  :rail-style="{ backgroundColor: '#e9ecef' }"
                  :dot-style="{ backgroundColor: '#007bff', border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }"
                  class="mb-1"
                  @change="updateInputFields"
                />
                <div class="d-flex justify-content-between align-items-center mt-2">
                  <div class="d-flex align-items-center">
                    <span class="text-muted small mr-1">HK$</span>
                    <b-form-input
                      v-model="update.filter_min_amount_input"
                      type="number"
                      min="0"
                      max="1000000"
                      step="any"
                      placeholder="0"
                      size="sm"
                      style="width: 80px;"
                      @input="updateMinAmount"
                    />
                  </div>
                  <span class="font-weight-bold text-primary mx-2">
                    HK${{ formatAmount(update.filter_amount_range[0]) }} - HK${{ formatAmount(update.filter_amount_range[1]) }}
                  </span>
                  <div class="d-flex align-items-center">
                    <span class="text-muted small mr-1">HK$</span>
                    <b-form-input
                      v-model="update.filter_max_amount_input"
                      type="number"
                      min="0"
                      max="1000000"
                      step="any"
                      placeholder="1000000"
                      size="sm"
                      style="width: 100px;"
                      @input="updateMaxAmount"
                    />
                  </div>
                </div>
              </div>
            </b-form-group>
          </b-col>

          <!-- 儲蓄/往來篩選 -->
          <b-col cols="12">
            <b-form-group
              label="儲蓄/往來："
              label-for="filter_is_savings"
              label-cols-md="3"
            >
              <v-select
                id="filter_is_savings"
                v-model="update.filter_is_savings"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="savingsOptions"
                :clearable="true"
                label="label"
                :reduce="(option) => option.value"
                placeholder="選擇儲蓄/往來"
              >
              </v-select>
            </b-form-group>
          </b-col>

          <!-- 存入支出篩選 -->
          <b-col cols="12">
            <b-form-group
              label="存入支出："
              label-for="filter_transaction_type"
              label-cols-md="3"
            >
              <v-select
                id="filter_transaction_type"
                v-model="update.filter_transaction_type"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="transactionTypeOptions"
                :clearable="true"
                label="label"
                :reduce="(option) => option.value"
                placeholder="選擇存入支出"
              >
              </v-select>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group
              label="進支方式："
              label-for="payment_method_id"
              label-cols-md="3"
            >
              <v-select
                id="payment_method_id"
                v-model="update.filter_payment_method_id"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="paymentMethodOptions"
                :clearable="true"
                label="name"
                :reduce="(option) => option.id"
                placeholder="選擇進支方式"
                :filter="paymentMethodSearch"
              >
              </v-select>
            </b-form-group>
          </b-col>

          <!-- 日期範圍篩選 -->
          <b-col cols="12">
            <b-form-group
              label="日期範圍："
              label-for="filter_start_date"
              label-cols-md="3"
            >
              <div class="d-flex align-items-center gap-2">
                <b-form-datepicker
                  id="filter_start_date"
                  v-model="update.filter_start_date"
                  :max="update.filter_end_date"
                  placeholder="開始日期"
                  :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                  locale="zh-TW"
                  :state="dateValidationState.start"
                  reset-button
                  close-button
                  class="flex-1"
                />
                <span class="text-muted px-2">至</span>
                <b-form-datepicker
                  id="filter_end_date"
                  v-model="update.filter_end_date"
                  :min="update.filter_start_date"
                  placeholder="結束日期"
                  :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                  locale="zh-TW"
                  :state="dateValidationState.end"
                  reset-button
                  close-button
                  class="flex-1"
                />
              </div>
              <b-form-invalid-feedback v-if="dateValidationState.start === false || dateValidationState.end === false">
                開始日期不能晚於結束日期
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
        <div class="d-flex mt-2 justify-content-end">
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="resetFilters"
            class="mr-1"
          >
            重置
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            type="button"
            variant="outline-danger"
            class="mr-1"
            @click="hide"
          >
            取消
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            type="submit"
          >
            <feather-icon size="16" class="mr-1" icon="CheckCircleIcon" />
            提交
          </b-button>
        </div>
      </b-form>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BButton,
  BModal,
  VBModal,
  BCardText,
  BOverlay,
  BForm,
  BFormGroup,
  BFormInput,
  BFormDatepicker,
  BFormInvalidFeedback,
  BRow,
  BCol,
  BCard,
  BCardBody,
} from "bootstrap-vue";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/antd.css";
import ability from "@/libs/acl/ability";

export default {
  components: {
    BButton,
    BModal,
    VBModal,
    BCardText,
    BOverlay,
    BForm,
    BFormGroup,
    BFormInput,
    BFormDatepicker,
    BFormInvalidFeedback,
    BRow,
    BCol,
    BCardBody,
    BCard,
    vSelect,
    VueSlider,
  },
  directives: {
    Ripple,
    "b-modal": VBModal,
  },
  props: {
    filter_payment_method_id: {
      type: [String, Number],
      default: null,
    },
    filter_start_date: {
      type: String,
      default: null,
    },
    filter_end_date: {
      type: String,
      default: null,
    },
    filter_min_amount: {
      type: [String, Number],
      default: null,
    },
    filter_max_amount: {
      type: [String, Number],
      default: null,
    },
    filter_is_savings: {
      type: [String, Boolean],
      default: null,
    },
    filter_transaction_type: {
      type: String,
      default: null,
    },
    paymentMethodOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      ability,
      loading: false,
      update: {
        filter_payment_method_id: this.filter_payment_method_id,
        filter_start_date: this.filter_start_date,
        filter_end_date: this.filter_end_date,
        filter_min_amount: this.filter_min_amount,
        filter_max_amount: this.filter_max_amount,
        filter_is_savings: this.filter_is_savings,
        filter_transaction_type: this.filter_transaction_type,
        filter_amount_range: [
          this.filter_min_amount || 0,
          this.filter_max_amount || 1000000,
        ],
        filter_min_amount_input: this.filter_min_amount || 0,
        filter_max_amount_input: this.filter_max_amount || 1000000,
      },
      // 儲蓄/往來選項
      savingsOptions: [
        { label: '往來', value: false },
        { label: '儲蓄', value: true },
      ],
      // 存入支出選項
      transactionTypeOptions: [
        { label: '存入', value: 'deposit' },
        { label: '支出', value: 'withdrawal' },
      ],
    };
  },
  computed: {
    dateValidationState() {
      const start = this.update.filter_start_date;
      const end = this.update.filter_end_date;

      if (!start || !end) {
        return { start: null, end: null };
      }

      const isValid = new Date(start) <= new Date(end);
      return {
        start: isValid,
        end: isValid,
      };
    },
  },
  methods: {
    onModalShown() {
      // 模態框顯示時的邏輯
    },
    hide() {
      this.$bvModal.hide("modal-bank-record-filter");
    },
    onSubmit() {
      this.$emit("update:filter_payment_method_id", this.update.filter_payment_method_id);
      this.$emit("update:filter_start_date", this.update.filter_start_date);
      this.$emit("update:filter_end_date", this.update.filter_end_date);
      this.$emit("update:filter_min_amount", this.update.filter_amount_range[0]);
      this.$emit("update:filter_max_amount", this.update.filter_amount_range[1]);
      this.$emit("update:filter_is_savings", this.update.filter_is_savings);
      this.$emit("update:filter_transaction_type", this.update.filter_transaction_type);
      this.$emit("refetch-data");
      this.hide();
    },
    resetFilters() {
      this.update = {
        filter_payment_method_id: null,
        filter_start_date: null,
        filter_end_date: null,
        filter_min_amount: null,
        filter_max_amount: null,
        filter_is_savings: null,
        filter_transaction_type: null,
        filter_amount_range: [0, 1000000],
        filter_min_amount_input: 0,
        filter_max_amount_input: 1000000,
      };
    },
    formatTooltip(value) {
      return `HK$${this.formatAmount(value)}`;
    },
    formatAmount(amount) {
      return Number(amount || 0).toLocaleString();
    },
    updateInputFields() {
      this.update.filter_min_amount_input = this.update.filter_amount_range[0];
      this.update.filter_max_amount_input = this.update.filter_amount_range[1];
    },
    updateMinAmount() {
      const min = Number(this.update.filter_min_amount_input) || 0;
      const max = this.update.filter_amount_range[1];
      if (min <= max) {
        this.update.filter_amount_range = [min, max];
      }
    },
    updateMaxAmount() {
      const min = this.update.filter_amount_range[0];
      const max = Number(this.update.filter_max_amount_input) || 1000000;
      if (max >= min) {
        this.update.filter_amount_range = [min, max];
      }
    },
    paymentMethodSearch(options, search) {
      return options.filter((item) => {
        return item.name.toLowerCase().includes(search.toLowerCase());
      });
    },
  },
};
</script>
